import React from 'react';
import { Row, Col } from 'antd';
import { Card } from '../../../components/CssFrameworkComponents';
import RenderingLocationCard from './renderingLocationCard';
import SaveIndicator from '../components/SaveIndicator';
import { useAutoSaveSetting } from '../hooks/useAutoSaveSetting';
import useFeatureFlag, { FeatureFlag } from '../../../hooks/useFeatureFlag';
import { usePrerenderUser, useCharbeePlanId } from '../../../hooks/usePrerenderUser';
import { ChargeBeePlanId } from '@prerender/billing-shared';
import UpgradePlanProvider, { useUpgradePlan } from '../../UpgradePlan/UpgradePlanProvider';
import RenderingLocationsCardLegacy from './legacy/renderingLocationsCard';

enum RenderingLocation {
  SHARED = 'shared',
  US = 'us',
  EU = 'eu',
}

function RenderingLocationsCard() {
  const useNewPricing = useFeatureFlag([FeatureFlag.Pricing2025]);
  if (useNewPricing) {
    return (
      <UpgradePlanProvider>
        <RenderingLocationsCardNewPricing />
      </UpgradePlanProvider>
    );
  }
  return <RenderingLocationsCardLegacy />;
}

function RenderingLocationsCardNewPricing() {
  const { settings } = usePrerenderUser();
  const { save, status, retry } = useAutoSaveSetting();
  const { openUpgradePlan } = useUpgradePlan();
  const chargebeePlanId = useCharbeePlanId();
  const isLocked = chargebeePlanId === ChargeBeePlanId.Starter;
  const currentLocation = settings.renderRegion;

  const handleUpgrade = (title: string) => {
    openUpgradePlan('rendering-locations', undefined, { ctaLocation: 'Rendering Locations', ctaName: title });
  };

  const availability: Record<RenderingLocation, 'current' | 'locked' | 'available'> = {
    shared: currentLocation === RenderingLocation.SHARED ? 'current' : 'available', // shared is always available
    us: currentLocation === RenderingLocation.US ? 'current' : isLocked ? 'locked' : 'available',
    eu: currentLocation === RenderingLocation.EU ? 'current' : isLocked ? 'locked' : 'available',
  };

  const handleSelect = (location: RenderingLocation) => {
    if (availability[location] !== 'available') return;
    save({ renderRegion: location });
  };

  return (
    <Card title="Rendering Location">
      <div className="col">
        <div className="row">
          <p>
            <span>
              Boost SEO, reduce latency, and improve accuracy by rendering pages closest to your visitors or crawlers.
            </span>
          </p>
        </div>
        <Row gutter={[16, 16]}>
          <Col lg={8} xs={24}>
            <RenderingLocationCard
              icon={'fe-globe'}
              title="Globally shared"
              description="Shared servers with no location-specific optimization."
              onSelect={() => handleSelect(RenderingLocation.SHARED)}
              onUpgrade={() => handleUpgrade('Globally shared')}
              availability={availability.shared}
            />
          </Col>
          <Col lg={8} xs={24}>
            <RenderingLocationCard
              icon={'fe-map-pin'}
              title="United States"
              description="Best for sites with high US traffic."
              onSelect={() => handleSelect(RenderingLocation.US)}
              onUpgrade={() => handleUpgrade('United States')}
              availability={availability.us}
            />
          </Col>
          <Col lg={8} xs={24}>
            <RenderingLocationCard
              icon={'fe-map-pin'}
              title="Europe"
              description="Best for sites with high EU traffic."
              onSelect={() => handleSelect(RenderingLocation.EU)}
              onUpgrade={() => handleUpgrade('Europe')}
              availability={availability.eu}
            />
          </Col>
        </Row>
        <div className="row">
          <SaveIndicator status={status} onRetry={retry} />
        </div>
      </div>
    </Card>
  );
}

export default RenderingLocationsCard;
